import { alpha, createTheme } from "@mui/material/styles";
import { blue, green, orange, red } from "@mui/material/colors";
import type {} from "@mui/x-date-pickers/themeAugmentation";

/**
 * `surface.<status>` palette tokens: a pale background tinting a row or card by its state
 * (an uploaded file's row is `surface.success`); normal text sits on it. Use the token in sx
 * (`bgcolor: "surface.success"`), never a fixed colour, so each colour scheme sets its own.
 */
interface SurfacePalette {
  success: string;
  info: string;
  warning: string;
  error: string;
}

declare module "@mui/material/styles" {
  /**
   * `background.sidebar`: the side menu and side panels, set apart from the page
   * (`background.default`). `background.paper` stays MUI's (dialogs, menus, popovers).
   */
  interface TypeBackground {
    sidebar: string;
  }
  interface Palette {
    surface: SurfacePalette;
  }
  interface PaletteOptions {
    surface?: SurfacePalette;
  }
}

const LIGHT_SURFACE: SurfacePalette = {
  success: green[50],
  info: blue[50],
  warning: orange[50],
  error: red[50],
};

const DARK_SURFACE: SurfacePalette = {
  success: alpha(green[400], 0.12),
  info: alpha(blue[400], 0.12),
  warning: alpha(orange[400], 0.12),
  error: alpha(red[400], 0.12),
};

const ROOT_FONT_SIZE_PX = 16;
/** Converts a px size to rem (against the browser's 16px root), honouring the user's font setting. */
const pxToRem = (px: number): string => {
  return `${px / ROOT_FONT_SIZE_PX}rem`;
};

/**
 * The app theme: light and dark colour schemes as CSS variables, switched at runtime with
 * `useColorScheme`. Read colours from sx tokens or `theme.vars`: `theme.palette` is always the
 * light value.
 */
export const theme = createTheme({
  cssVariables: { colorSchemeSelector: "class" },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: "#0b3d5c" },
        secondary: { main: "#2f8f9d" },
        background: {
          default: "#f3f5f7",
          paper: "#ffffff",
          sidebar: "#ffffff",
        },
        surface: LIGHT_SURFACE,
      },
    },
    dark: {
      palette: {
        primary: { main: "#7fb6d8" },
        secondary: { main: "#5cc0cc" },
        background: {
          default: "#10161c",
          paper: "#161e25",
          sidebar: "#0b1014",
        },
        surface: DARK_SURFACE,
      },
    },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: ["Roboto", "system-ui", "Arial", "sans-serif"].join(","),
    body1: { fontSize: pxToRem(14) },
    h1: { fontSize: pxToRem(28), fontWeight: 600 },
    h2: { fontSize: pxToRem(20), fontWeight: 600 },
    h3: { fontSize: pxToRem(16), fontWeight: 600 },
    h4: { fontSize: pxToRem(14), fontWeight: 600 }
  },
  components: {
    MuiDatePicker: {
      defaultProps: {
        format: "dd MMMM yyyy",
      },
    },
  },
});
